import { motion } from "framer-motion";
import {
  Camera, Smartphone, AudioLines, ShieldAlert,
  CheckCircle, Timer, Trophy, Users, Minus,
  Brain, Lightbulb, TrendingUp,
} from "lucide-react";

const showcases = [
  {
    title: "AI-Powered Proctoring",
    subtitle: "Exam integrity, guaranteed.",
    description: "Our AI proctoring system monitors exams in real-time using computer vision and audio analysis — so you can trust every result.",
    features: [
      { icon: Camera, text: "Webcam monitoring with face detection" },
      { icon: Smartphone, text: "Electronic device detection via COCO-SSD" },
      { icon: AudioLines, text: "Audio anomaly analysis using FFT" },
      { icon: ShieldAlert, text: "Auto-submit after 3 critical violations" },
    ],
  },
  {
    title: "Smart Exam Engine",
    subtitle: "Create exams in minutes, not hours.",
    description: "A powerful exam builder with auto-grading, negative marking, and instant classroom assignment — everything a teacher needs.",
    features: [
      { icon: CheckCircle, text: "Auto-grading with detailed analytics" },
      { icon: Minus, text: "Configurable negative marking" },
      { icon: Timer, text: "Timed exams with countdown" },
      { icon: Trophy, text: "Real-time leaderboards & rankings" },
    ],
  },
  {
    title: "AI-Powered Learning",
    subtitle: "Your personal tutor, always on.",
    description: "From instant doubt solving to AI-generated solutions and performance insights — learning never stops on EduLabs.",
    features: [
      { icon: Brain, text: "AI tutor for any subject, any time" },
      { icon: Lightbulb, text: "Auto-generated question solutions" },
      { icon: TrendingUp, text: "Performance insights & projections" },
      { icon: Users, text: "1-on-1 mentorship with verified teachers" },
    ],
  },
];

const USPShowcase = () => {
  return (
    <section className="py-24 px-6 bg-background">
      <div className="container mx-auto space-y-32">
        {showcases.map((item, index) => {
          const isReversed = index % 2 !== 0;
          return (
            <div
              key={index}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? "lg:direction-rtl" : ""}`}
            >
              {/* Text side */}
              <motion.div
                initial={{ opacity: 0, x: isReversed ? 40 : -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`${isReversed ? "lg:order-2" : ""}`}
              >
                <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">
                  {item.subtitle}
                </p>
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground leading-tight mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {item.description}
                </p>
              </motion.div>

              {/* Features grid side */}
              <motion.div
                initial={{ opacity: 0, x: isReversed ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className={`grid grid-cols-2 gap-4 ${isReversed ? "lg:order-1" : ""}`}
              >
                {item.features.map((feature, fIndex) => (
                  <motion.div
                    key={fIndex}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.2 + fIndex * 0.1 }}
                    className="bg-card border rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {feature.text}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default USPShowcase;
